import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ArrowRightLeft, TrendingUp, TrendingDown, Calendar, Loader2, Users } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TradeDetailsModal = ({ isOpen, onClose, trade, token }) => {
  const [fromProfEvents, setFromProfEvents] = useState([]);
  const [toProfEvents, setToProfEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  const fetchProfessorEvents = useCallback(async () => {
    if (!trade?.from_professor_id || !trade?.to_professor_id) return;

    setIsLoadingEvents(true);
    try {
      const [fromRes, toRes] = await Promise.all([
        fetch(`${API_URL}/api/events/${trade.from_professor_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/events/${trade.to_professor_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (fromRes.ok) {
        const fromData = await fromRes.json();
        setFromProfEvents(fromData.slice(0, 5)); // Ultimi 5 eventi
      }
      if (toRes.ok) {
        const toData = await toRes.json();
        setToProfEvents(toData.slice(0, 5)); // Ultimi 5 eventi
      }
    } catch (err) {
      console.error('Error fetching professor events:', err);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [trade?.from_professor_id, trade?.to_professor_id, token]);

  useEffect(() => {
    if (isOpen && trade) {
      fetchProfessorEvents();
    }
  }, [isOpen, trade, fetchProfessorEvents]);

  if (!trade) return null;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">In Attesa</Badge>;
      case 'accepted':
        return <Badge className="bg-green-500">Accettato</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Rifiutato</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500">Annullato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventIcon = (points) => {
    if (points > 0) return <TrendingUp className="h-3 w-3 text-green-600" />;
    return <TrendingDown className="h-3 w-3 text-red-600" />;
  };

  const ProfessorCard = ({ professor, events, side }) => {
    const bgColor = side === 'from' ? 'from-blue-50 to-blue-100' : 'from-cyan-50 to-cyan-100';
    const borderColor = side === 'from' ? 'border-blue-200' : 'border-cyan-200';
    const textColor = side === 'from' ? 'text-blue-700' : 'text-cyan-700';

    return (
      <Card className={`bg-gradient-to-br ${bgColor} ${borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className={`h-16 w-16 border-2 ${borderColor}`}>
              <AvatarFallback className={`${bgColor.replace('from-', 'bg-').split(' ')[0]} ${textColor} font-bold text-lg`}>
                {professor.name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-bold text-lg">{professor.name}</h3>
              <p className="text-sm text-muted-foreground">{professor.subject}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`p-3 rounded-lg bg-white border ${borderColor}`}>
              <div className="text-xs text-muted-foreground mb-1">Punti Totali</div>
              <div className={`text-2xl font-bold ${textColor}`}>
                {Math.round(professor.points || 0)}
              </div>
            </div>
            <div className={`p-3 rounded-lg bg-white border ${borderColor}`}>
              <div className="text-xs text-muted-foreground mb-1">Eventi</div>
              <div className={`text-2xl font-bold ${textColor}`}>
                {events.length}
              </div>
            </div>
          </div>

          {/* Ultimi Eventi */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground mb-2">ULTIMI 5 EVENTI</div>
            {isLoadingEvents ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-4">
                Nessun evento recente
              </div>
            ) : (
              <div className="space-y-1.5">
                {events.map((event, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getEventIcon(event.points)}
                      <span className="text-xs truncate">{event.description}</span>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap ml-2 ${
                      event.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {event.points > 0 ? '+' : ''}{event.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            Dettagli Scambio
            {getStatusBadge(trade.status)}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="professors" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="professors">Professori</TabsTrigger>
            <TabsTrigger value="teams">Team</TabsTrigger>
            <TabsTrigger value="timeline">Cronologia</TabsTrigger>
          </TabsList>

          {/* Tab Professori */}
          <TabsContent value="professors" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[450px]">
              <div className="grid md:grid-cols-2 gap-4 pr-4">
                <div>
                  <div className="text-sm font-semibold text-blue-600 mb-2">DA SCAMBIARE</div>
                  <ProfessorCard 
                    professor={{
                      name: trade.from_professor_name,
                      subject: trade.from_professor_subject,
                      points: trade.from_professor_points
                    }}
                    events={fromProfEvents}
                    side="from"
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-cyan-600 mb-2">IN CAMBIO DI</div>
                  <ProfessorCard 
                    professor={{
                      name: trade.to_professor_name,
                      subject: trade.to_professor_subject,
                      points: trade.to_professor_points
                    }}
                    events={toProfEvents}
                    side="to"
                  />
                </div>
              </div>

              {/* Confronto Diretto */}
              <Card className="mt-4 mr-4 border-2 border-purple-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    Confronto Punti Attuali
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100">
                      <div className="text-5xl font-bold text-blue-600 mb-2 animate-pulse">
                        {Math.round(trade.from_professor_points || 0)}
                      </div>
                      <div className="text-sm font-semibold text-blue-700 mb-1">
                        {trade.from_professor_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {trade.from_professor_subject}
                      </div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-gradient-to-br from-cyan-50 to-cyan-100">
                      <div className="text-5xl font-bold text-cyan-600 mb-2 animate-pulse">
                        {Math.round(trade.to_professor_points || 0)}
                      </div>
                      <div className="text-sm font-semibold text-cyan-700 mb-1">
                        {trade.to_professor_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {trade.to_professor_subject}
                      </div>
                    </div>
                  </div>
                  
                  {/* Differenza Punti */}
                  <div className="text-center mb-4">
                    {trade.from_professor_points > trade.to_professor_points ? (
                      <Badge className="bg-blue-500 text-white px-4 py-2 text-base">
                        {trade.from_professor_name} ha {(trade.from_professor_points - trade.to_professor_points)} punti in più
                      </Badge>
                    ) : trade.to_professor_points > trade.from_professor_points ? (
                      <Badge className="bg-cyan-500 text-white px-4 py-2 text-base">
                        {trade.to_professor_name} ha {(trade.to_professor_points - trade.from_professor_points)} punti in più
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-500 text-white px-4 py-2 text-base">
                        Stessi punti
                      </Badge>
                    )}
                  </div>

                  {/* Spiegazione Scambio */}
                  <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200">
                    <div className="flex items-start gap-3">
                      <ArrowRightLeft className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5 animate-bounce" />
                      <div className="flex-1">
                        <h4 className="font-bold text-orange-900 mb-2">⚠️ Come Funziona lo Scambio</h4>
                        <div className="space-y-2 text-sm text-orange-800">
                          <div className="flex items-start gap-2">
                            <span className="font-bold">✅</span>
                            <p>I <strong>PROFESSORI vengono scambiati</strong> tra i team</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-bold">✅</span>
                            <p>Ogni team riceve il professore dell'altro con i <strong>suoi punti attuali</strong></p>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-bold">❌</span>
                            <p>I <strong>punti NON vengono trasferiti</strong> da un team all'altro</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-bold">📊</span>
                            <p>I <strong>punti totali dei team</strong> vengono ricalcolati sommando i punti di tutti i loro professori</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </ScrollArea>
          </TabsContent>

          {/* Tab Teams */}
          <TabsContent value="teams" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[450px]">
              <div className="space-y-4 pr-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="h-6 w-6 text-blue-600" />
                      <h3 className="font-bold text-lg">Team Proponente</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 border-2 border-blue-300">
                          <AvatarFallback className="bg-blue-200 text-blue-700 font-bold">
                            {trade.from_team_name?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold">{trade.from_team_name}</div>
                          <div className="text-sm text-muted-foreground">
                            Proposto da: {trade.from_user_name}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-white border border-blue-200">
                          <div className="text-xs text-muted-foreground">Punti Totali Team</div>
                          <div className="text-xl font-bold text-blue-700">
                            {trade.from_team_points || '0.0'}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-white border border-blue-200">
                          <div className="text-xs text-muted-foreground">Professori</div>
                          <div className="text-xl font-bold text-blue-700">
                            {trade.from_team_professors || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-center">
                  <ArrowRightLeft className="h-8 w-8 text-gray-400" />
                </div>

                <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="h-6 w-6 text-cyan-600" />
                      <h3 className="font-bold text-lg">Team Ricevente</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 border-2 border-cyan-300">
                          <AvatarFallback className="bg-cyan-200 text-cyan-700 font-bold">
                            {trade.to_team_name?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold">{trade.to_team_name}</div>
                          <div className="text-sm text-muted-foreground">
                            Destinatario: {trade.to_user_name}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-white border border-cyan-200">
                          <div className="text-xs text-muted-foreground">Punti Totali Team</div>
                          <div className="text-xl font-bold text-cyan-700">
                            {trade.to_team_points || '0.0'}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-white border border-cyan-200">
                          <div className="text-xs text-muted-foreground">Professori</div>
                          <div className="text-xl font-bold text-cyan-700">
                            {trade.to_team_professors || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Cronologia */}
          <TabsContent value="timeline" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[450px]">
              <div className="space-y-4 pr-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-purple-600" />
                      Storia Scambio
                    </h3>
                    <div className="space-y-4">
                      {/* Proposta */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <ArrowRightLeft className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">Proposta Inviata</div>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(trade.created_at)}
                          </div>
                          <div className="text-sm mt-1">
                            {trade.from_user_name} ha proposto uno scambio a {trade.to_user_name}
                          </div>
                        </div>
                      </div>

                      {/* Status Attuale */}
                      {trade.status !== 'pending' && (
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            trade.status === 'accepted' ? 'bg-green-100' :
                            trade.status === 'rejected' ? 'bg-red-100' :
                            'bg-gray-100'
                          }`}>
                            {trade.status === 'accepted' ? '✓' : trade.status === 'rejected' ? '✗' : '—'}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold">
                              {trade.status === 'accepted' ? 'Scambio Accettato' :
                               trade.status === 'rejected' ? 'Scambio Rifiutato' :
                               'Scambio Annullato'}
                            </div>
                            {trade.updated_at && (
                              <div className="text-sm text-muted-foreground">
                                {formatDate(trade.updated_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Info Aggiuntive */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Informazioni Scambio</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ID Scambio:</span>
                        <span className="font-mono text-xs">{trade.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stato:</span>
                        <span className="font-semibold">{getStatusBadge(trade.status)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Data Proposta:</span>
                        <span>{formatDate(trade.created_at)}</span>
                      </div>
                      {trade.updated_at && trade.updated_at !== trade.created_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Ultimo Aggiornamento:</span>
                          <span>{formatDate(trade.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
